<template>
  <v-select
    ref="vSelect"
    v-model="selected"
    :options="options"
    :filterable="false"
    :placeholder="placeholder"
    :selectable="item => !item.disabled && !disabled"
    :disabled="disabled"
    :reduce="reduce"
    :loading="loading && options.length === 0"
    :label="optionLabel"
    :clearable="clearable"
    :append-to-body="appendToBody"
    :calculate-position="calculatePosition"
    @open="onOpen"
    @close="onClose"
    @search="onSearch"
  >
    <template #option="option">
      <slot
        name="optionValue"
        :value="option"
      >
        {{ option[optionLabel] }}
      </slot>
    </template>
    <template #no-options="{ search, searching }">
      <slot
        name="no-options"
        :search="search"
        :searching="searching"
      >
        <template v-if="searching">
          <em>'{{ search }}'</em> tidak ditemukan
        </template>
        <em v-else>Masukkan keyword yang valid</em>
      </slot>
    </template>
    <template #list-footer>
      <li
        v-show="hasNextPage"
        ref="load"
        class="text-center flex justify-center items-center gap-2 h-[50px]"
      >
        <b-spinner
          small
          variant="secondary"
        />
        <span>Loading more options...</span>
      </li>
    </template>
  </v-select>
</template>

<script>
/**
 * Select component with infinite scroll capability.
 *
 * ## Props
 * @prop {Object | string | Number} defaultValue - The default value of the selected item.
 * @prop {Object | String | Number} value - The value of the selected item.
 * @prop {String} optionLabel - The Object key used to display the selected item.
 * @prop {Array} options - The list of items available for selection.
 * @prop {String} placeholder - A short guidance text displayed in the input field.
 * @prop {Boolean} hasNextPage - Indicates whether there are more pages of data available for loading.
 * @prop {Number} debounceTimeout - Delay time (in milliseconds) for search functionality.
 * @prop {Function} reduce - A function that transforms the input value before returning it.
 * @prop {Boolean} loading - Manually set the v-select's loading state.
 * @prop {Boolean} clearable - Indicates whether the select input can be cleared.
 * @prop {Boolean} appendToBody - Indicates whether the select input should be appended to the body.
 * @prop {Function} calculatePosition - A function that calculates the position of the select input.
 * @prop {Boolean} disabled - Indicates whether the select input is disabled.
 * @prop {Boolean} reduce - A function that transforms the input value before returning it.
 * @prop {Boolean} loading - Manually set the v-select's loading state.
 *
 * ## Events
 * @event input - Triggered when a user selects an item.
 * @param {Object | String | Number} payload - The selected item.
 *
 * @event on-search - Triggered when a user uses the search functionality of vue-select.
 * @param {String} query - The search query entered by the user.
 *
 * @event on-intersect - Triggered when the scroll position reaches the bottom of the container.
 *
 * @event on-close - Triggered when the container closed.
 */

import _ from 'lodash'
import vSelect from 'vue-select'
import { BSpinner } from 'bootstrap-vue'

export default {
  name: 'InfiniteSelect',
  components: {
    vSelect,
    BSpinner,
  },
  props: {
    defaultValue: {
      type: [Object, String, Number, null],
      required: false,
      default: null,
    },
    value: {
      type: [Object, String, Number, null],
      required: false,
      default: null,
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
    options: {
      type: Array,
      default: () => [],
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Pilih item',
    },
    hasNextPage: {
      type: Boolean,
      default: false,
      required: true,
    },
    debounceTimeout: {
      type: Number,
      default: 300,
      required: false,
    },
    reduce: {
      type: Function,
      default: option => option,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    appendToBody: {
      type: Boolean,
      default: false,
      required: false,
    },
    calculatePosition: {
      type: Function,
      default: () => {},
      required: false,
    },
  },
  data: () => ({
    observer: null,
    observerRoot: null,
    debounceSearch: null,
    lastScollPosition: 0,
    isOnSearch: false,
    searchQuery: '',
  }),
  computed: {
    selected: {
      get() {
        return this.normalizeSelection(this.value ?? _.cloneDeep(this.defaultValue) ?? null)
      },
      set(newValue) {
        this.$emit('input', this.normalizeSelection(newValue))
      },
    },
  },
  watch: {
    options: {
      handler() {
        this.$nextTick(() => {
          this.restoreScrollPosition()
          this.observeLoadTrigger()
        })
      },
      deep: false,
    },
    hasNextPage: {
      handler(hasMore) {
        if (hasMore) {
          this.observeLoadTrigger()
        } else if (this.observer && this.$refs.load) {
          this.observer.unobserve(this.$refs.load)
        }
      },
    },
    loading: {
      handler(isLoading) {
        if (!isLoading && this.hasNextPage) {
          this.observeLoadTrigger()
        }
      },
    },
  },
  created() {
    this.debounceSearch = _.debounce(
      q => {
        const trimmed = q ? q.trim() : ''
        this.isOnSearch = Boolean(trimmed)
        this.searchQuery = trimmed
        this.$emit('on-search', trimmed)
      },
      this.debounceTimeout,
    )
  },
  beforeDestroy() {
    if (this.debounceSearch?.cancel) {
      this.debounceSearch.cancel()
    }
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    this.observerRoot = null
  },
  methods: {
    getDropdownMenu() {
      return (this.$refs.vSelect && this.$refs.vSelect.$refs && this.$refs.vSelect.$refs.dropdownMenu)
        || (this.$refs.load && this.$refs.load.parentElement)
        || (this.$el ? this.$el.querySelector('.vs__dropdown-menu') : null)
    },
    restoreScrollPosition() {
      if (!this.isOnSearch && this.lastScollPosition > 0) {
        const dropdown = this.getDropdownMenu()
        if (dropdown) {
          dropdown.scrollTop = this.lastScollPosition
          this.lastScollPosition = 0
        }
      }
    },
    observeLoadTrigger() {
      if (!this.hasNextPage) return
      const attach = () => {
        const loadEl = this.$refs.load
        if (!loadEl || !this.hasNextPage) return false
        const ul = loadEl.parentElement || this.getDropdownMenu()
        if (ul && this.observerRoot !== ul) {
          if (this.observer) {
            this.observer.disconnect()
          }
          this.observerRoot = ul
          this.observer = new IntersectionObserver(this.infiniteScroll, { root: ul })
        } else if (!this.observer) {
          this.observer = new IntersectionObserver(this.infiniteScroll)
        }
        this.observer.unobserve(loadEl)
        this.observer.observe(loadEl)
        return true
      }
      this.$nextTick(() => {
        if (!attach()) {
          this.$nextTick(attach)
        }
      })
    },
    onSearch(q) {
      this.lastScollPosition = 0
      const trimmed = q ? q.trim() : ''
      if (!trimmed && !this.searchQuery) {
        if (this.debounceSearch?.cancel) {
          this.debounceSearch.cancel()
        }
        this.observeLoadTrigger()
        return
      }
      if (this.debounceSearch) {
        this.debounceSearch(trimmed)
      }
    },
    normalizeSelection(val) {
      if (_.isObject(val)) {
        return ((_.get(val, this.optionLabel) ?? '') !== '') ? val : null
      }
      if (_.isString(val)) {
        return val.trim() !== '' ? val : null
      }
      return val ?? null
    },
    onOpen() {
      this.observeLoadTrigger()
    },
    onClose() {
      if (this.debounceSearch?.cancel) {
        this.debounceSearch.cancel()
      }
      this.lastScollPosition = 0
      if (this.searchQuery) {
        this.isOnSearch = false
        this.searchQuery = ''
        this.$emit('on-search', '')
      }
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
      this.observerRoot = null
      this.$emit('on-close')
    },
    async infiniteScroll(entries) {
      const entry = entries.find(e => e.isIntersecting)
      if (entry && this.hasNextPage && !this.loading) {
        this.observer.unobserve(entry.target)
        if (this.isOnSearch) this.isOnSearch = false
        const ul = entry.target.parentElement || this.getDropdownMenu()
        if (!ul) return
        this.lastScollPosition = ul.scrollTop
        this.$emit('on-intersect')
      }
    },
  },
}
</script>
